import { useId } from 'react';

import { GLYPH_PATH, SQUIRCLE_PATH } from './paths';

import type { LogoProps, WordmarkProps } from './Logo.types';

/**
 * The Dō mark — the 道 glyph on the sakura gradient continuous-corner squircle.
 *
 * Rendered as live, resolution-independent vector (the glyph is outlined, so no
 * CJK webfont is required) and driven entirely by the `--color-do-mark-*` brand
 * tokens, keeping it pixel-identical to the exported favicon and app icons.
 *
 * ```tsx
 * <Logo size={64} />
 * <Logo size={40} mask="circle" />
 * <Logo size={16} decorative />
 * ```
 */
export function Logo({
  size = 40,
  mask = 'squircle',
  glyphFill = 'var(--color-do-mark-glyph)',
  title = 'Dō',
  decorative = false,
  ...props
}: LogoProps) {
  const gradientId = useId();

  const a11y = decorative
    ? ({ 'aria-hidden': true } as const)
    : ({ role: 'img', 'aria-label': title } as const);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 1000 1000"
      xmlns="http://www.w3.org/2000/svg"
      {...a11y}
      {...props}
    >
      {!decorative && <title>{title}</title>}
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--color-do-mark-1)" />
          <stop offset="0.5" stopColor="var(--color-do-mark-2)" />
          <stop offset="1" stopColor="var(--color-do-mark-3)" />
        </linearGradient>
      </defs>

      {mask === 'squircle' && (
        <path d={SQUIRCLE_PATH} fill={`url(#${gradientId})`} />
      )}
      {mask === 'circle' && (
        <circle cx="500" cy="500" r="500" fill={`url(#${gradientId})`} />
      )}
      {mask === 'square' && (
        <rect width="1000" height="1000" fill={`url(#${gradientId})`} />
      )}
      {/* `none` draws no tile — the glyph alone, on whatever is behind it. */}

      <path d={GLYPH_PATH} fill={glyphFill} />
    </svg>
  );
}

/**
 * The Dō wordmark lockup — the {@link Logo} mark beside (or above) the "Dō"
 * lettering set in Apfel Grotezk, the display face.
 *
 * ```tsx
 * <Wordmark size={48} />
 * <Wordmark size={40} orientation="stacked" />
 * <Wordmark size={64} tone="dark" />  // on a light surface
 * ```
 */
export function Wordmark({
  size = 48,
  tone = 'light',
  orientation = 'horizontal',
  title = 'Dō',
  className = '',
  ...props
}: WordmarkProps) {
  const stacked = orientation === 'stacked';

  return (
    <span
      className={`inline-flex ${
        stacked ? 'flex-col' : 'flex-row'
      } items-center font-display font-bold leading-none tracking-tight ${
        tone === 'light' ? 'text-neutral-50' : 'text-deep-900'
      } ${className}`.trim()}
      style={{
        // Lettering scales with the mark; gaps derive from the mark size.
        gap: stacked ? size * 0.28 : size * 0.34,
      }}
      {...props}
    >
      <Logo size={size} decorative />
      <span
        aria-label={title}
        style={{ fontSize: stacked ? size * 0.62 : size * 0.92 }}
      >
        Dō
      </span>
    </span>
  );
}

export default Logo;
